import React, { useCallback, useEffect, useState } from "react";
import { Modal, Button, Form, Row, Col, Alert } from "react-bootstrap";
import { FormattedMessage, useIntl } from "react-intl";
import {
  UpgradeTarget,
  TargetTypeEnum,
  GuaranteeAppliedEnum,
  PenaltyAppliedEnum,
  MealPlanUpgradeModeEnum,
} from "../domain/upgradeConfiguration.schema";
import { upgradeManagerMessages } from "../upgradeManager.messages";
import { useInventoryOptions } from "../hooks/useInventoryOptions.hook";
import { useDefaultCurrency } from "../hooks/useDefaultCurrency.hook";
import { DateRangesInput } from "./DateRangesInput";
import { DeltaFields } from "./DeltaFields";
import { StyledModalBody } from "../styles";
import { hasBothDates } from "../utils/validation";
import { getBaseTicker } from "../utils/formatters";

interface TargetModalProps {
  show: boolean;
  target?: UpgradeTarget;
  onHide: () => void;
  onSave: (target: UpgradeTarget) => void;
}

const setBaseTicker = (
  target: UpgradeTarget,
  value: string,
  targetType: TargetTypeEnum
): Partial<UpgradeTarget> => {
  if (targetType === TargetTypeEnum.ACCOMMODATION) {
    return { ...target, baseAccommodationTicker: value, baseMealPlanTicker: undefined };
  }
  return { ...target, baseMealPlanTicker: value, baseAccommodationTicker: undefined };
};

export function TargetModal({ show, target, onHide, onSave }: TargetModalProps) {
  const intl = useIntl();
  const messages = upgradeManagerMessages;
  const {
    accommodations,
    mealPlans,
    loadingAccommodations,
    loadingMealPlans,
  } = useInventoryOptions();
  const { defaultCurrency } = useDefaultCurrency();

  const [formData, setFormData] = useState<UpgradeTarget>({
    targetType: TargetTypeEnum.ACCOMMODATION,
    baseAccommodationTicker: "",
    upgradeableToTickers: [],
    sellingActivationRule: {
      penaltyApplied: PenaltyAppliedEnum.IGNORE,
      guaranteeApplied: GuaranteeAppliedEnum.IGNORE,
      excludedDates: [],
      includedDates: [],
      includedTags: [],
      promoCodes: [],
    },
  });

  const [error, setError] = useState<string | null>(null);

  const isAccommodation = formData.targetType === TargetTypeEnum.ACCOMMODATION;
  const availableOptions = isAccommodation ? accommodations : mealPlans;
  const isLoading = isAccommodation ? loadingAccommodations : loadingMealPlans;

  useEffect(() => {
    if (target) {
      setFormData(target);
    } else {
      setFormData({
        targetType: TargetTypeEnum.ACCOMMODATION,
        baseAccommodationTicker: "",
        upgradeableToTickers: [],
        sellingActivationRule: {
          penaltyApplied: PenaltyAppliedEnum.IGNORE,
          guaranteeApplied: GuaranteeAppliedEnum.IGNORE,
          excludedDates: [],
          includedDates: [],
          includedTags: [],
          promoCodes: [],
        },
      });
    }
    setError(null);
  }, [target, show]);

  const handleSave = useCallback(() => {
    const baseTicker = getBaseTicker(formData);
    if (!baseTicker) {
      setError(intl.formatMessage(messages.formErrors));
      return;
    }
    if (!formData.upgradeableToTickers || formData.upgradeableToTickers.length === 0) {
      setError(intl.formatMessage(messages.formErrors));
      return;
    }
    if (!isAccommodation && !formData.mealPlanUpgradeMode) {
      setError(intl.formatMessage(messages.formErrors));
      return;
    }

    if (isAccommodation && hasBothDates(formData.sellingActivationRule ?? {})) {
      setError(intl.formatMessage(messages.errorBothDates));
      return;
    }

    onSave(formData);
    setError(null);
  }, [formData, isAccommodation, onSave, intl, messages]);

  return (
    <Modal show={show} onHide={onHide} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>
          <FormattedMessage {...messages.editTarget} />
        </Modal.Title>
      </Modal.Header>
      <StyledModalBody>
        <Form>
          <Form.Group as={Row} className="mb-3">
            <Form.Label column sm={4}>
              <FormattedMessage {...messages.targetType} /> *
            </Form.Label>
            <Col sm={8}>
              <Form.Control
                as="select"
                value={formData.targetType}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  const newType = e.target.value as TargetTypeEnum;
                  setFormData(prev => ({
                    ...prev,
                    targetType: newType,
                    baseAccommodationTicker: "",
                    upgradeableToTickers: [],
                    ...(newType === TargetTypeEnum.MEAL_PLAN && {
                      mealPlanUpgradeMode: MealPlanUpgradeModeEnum.EXTRA,
                      sellingActivationRule: {
                        penaltyApplied: PenaltyAppliedEnum.IGNORE,
                        guaranteeApplied: GuaranteeAppliedEnum.IGNORE,
                        excludedDates: [],
                        includedDates: [],
                        includedTags: [],
                        promoCodes: [],
                      },
                    }),
                    ...(newType === TargetTypeEnum.ACCOMMODATION && {
                      mealPlanUpgradeMode: undefined,
                    }),
                  }));
                }}>
                <option value={TargetTypeEnum.ACCOMMODATION}>
                  {intl.formatMessage(messages.targetTypeAccommodation)}
                </option>
                <option value={TargetTypeEnum.MEAL_PLAN}>
                  {intl.formatMessage(messages.targetTypeMealPlan)}
                </option>
              </Form.Control>
            </Col>
          </Form.Group>

          <Form.Group as={Row} className="mb-3">
            <Form.Label column sm={4}>
              <FormattedMessage {...messages.base} /> *
            </Form.Label>
            <Col sm={8}>
              <Form.Control
                as="select"
                value={getBaseTicker(formData)}
                disabled={isLoading || availableOptions.length === 0}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  const updatedTarget = setBaseTicker(
                    formData,
                    e.target.value,
                    formData.targetType
                  );
                  setFormData(prev => ({ ...prev, ...updatedTarget }));
                }}>
                <option value="">
                  {isLoading
                    ? intl.formatMessage(messages.loading)
                    : intl.formatMessage(messages.selectOption)}
                </option>
                {availableOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </Form.Control>
              {formData.targetType === TargetTypeEnum.ACCOMMODATION &&
                !isLoading &&
                accommodations.length === 0 && (
                  <Form.Text className="text-warning">
                    <FormattedMessage {...messages.noAccommodationsWarning} />
                  </Form.Text>
                )}
            </Col>
          </Form.Group>

          <Form.Group as={Row} className="mb-3">
            <Form.Label column sm={4}>
              <FormattedMessage {...messages.upgradeTo} /> *
            </Form.Label>
            <Col sm={8}>
              <Form.Control
                as="select"
                multiple
                value={formData.upgradeableToTickers}
                disabled={isLoading || availableOptions.length === 0}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                  const selected = Array.from(
                    ((e.target as unknown) as HTMLSelectElement).selectedOptions,
                    option => option.value
                  );
                  setFormData(prev => ({ ...prev, upgradeableToTickers: selected }));
                }}
                style={{ minHeight: "120px" }}>
                {availableOptions
                  .filter(opt => opt.value !== getBaseTicker(formData))
                  .map(option => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
              </Form.Control>
              <Form.Text className="text-muted">
                <FormattedMessage {...messages.multiSelectHelp} />
              </Form.Text>
            </Col>
          </Form.Group>

          {!isAccommodation && (
            <Form.Group as={Row} className="mb-3">
              <Form.Label column sm={4}>
                <FormattedMessage {...messages.mealPlanUpgradeMode} /> *
              </Form.Label>
              <Col sm={8}>
                <Form.Control
                  as="select"
                  value={formData.mealPlanUpgradeMode || MealPlanUpgradeModeEnum.EXTRA}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setFormData(prev => ({
                      ...prev,
                      mealPlanUpgradeMode: e.target.value as MealPlanUpgradeModeEnum,
                    }))
                  }>
                  <option value={MealPlanUpgradeModeEnum.EXTRA}>
                    {intl.formatMessage(messages.mealPlanUpgradeModeExtra)}
                  </option>
                  <option value={MealPlanUpgradeModeEnum.INVENTORY}>
                    {intl.formatMessage(messages.mealPlanUpgradeModeInventory)}
                  </option>
                </Form.Control>
              </Col>
            </Form.Group>
          )}

          {isAccommodation && (
            <>
              <DeltaFields
                label={<FormattedMessage {...messages.minDelta} />}
                delta={formData.sellingActivationRule?.minAmountDelta}
                onChange={delta =>
                  setFormData(prev => ({
                    ...prev,
                    sellingActivationRule: {
                      ...prev.sellingActivationRule!,
                      minAmountDelta: delta,
                    },
                  }))
                }
                defaultCurrency={defaultCurrency}
                labelCol={4}
                selectCol={8}
                valueCol={6}
                percentageCol={8}
                currencyCol={2}
                spacing="mb-3"
              />

              <DeltaFields
                label={<FormattedMessage {...messages.maxDelta} />}
                delta={formData.sellingActivationRule?.maxAmountDelta}
                onChange={delta =>
                  setFormData(prev => ({
                    ...prev,
                    sellingActivationRule: {
                      ...prev.sellingActivationRule!,
                      maxAmountDelta: delta,
                    },
                  }))
                }
                defaultCurrency={defaultCurrency}
                labelCol={4}
                selectCol={8}
                valueCol={6}
                percentageCol={8}
                currencyCol={2}
                spacing="mb-3"
              />

              <Form.Group as={Row} className="mb-3">
                <Form.Label column sm={4}>
                  <FormattedMessage {...messages.guarantee} />
                </Form.Label>
                <Col sm={8}>
                  <Form.Control
                    as="select"
                    value={
                      formData.sellingActivationRule?.guaranteeApplied ||
                      GuaranteeAppliedEnum.IGNORE
                    }
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      setFormData(prev => ({
                        ...prev,
                        sellingActivationRule: {
                          ...prev.sellingActivationRule!,
                          guaranteeApplied: e.target.value as GuaranteeAppliedEnum,
                        },
                      }))
                    }>
                    <option value={GuaranteeAppliedEnum.IGNORE}>
                      {intl.formatMessage(messages.guaranteeIgnore)}
                    </option>
                    <option value={GuaranteeAppliedEnum.HAS_GUARANTEE_APPLIED}>
                      {intl.formatMessage(messages.guaranteeHas)}
                    </option>
                    <option value={GuaranteeAppliedEnum.NO_GUARANTEE_APPLIED}>
                      {intl.formatMessage(messages.guaranteeNo)}
                    </option>
                  </Form.Control>
                </Col>
              </Form.Group>

              <Form.Group as={Row} className="mb-3">
                <Form.Label column sm={4}>
                  <FormattedMessage {...messages.penalty} />
                </Form.Label>
                <Col sm={8}>
                  <Form.Control
                    as="select"
                    value={
                      formData.sellingActivationRule?.penaltyApplied || PenaltyAppliedEnum.IGNORE
                    }
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      setFormData(prev => ({
                        ...prev,
                        sellingActivationRule: {
                          ...prev.sellingActivationRule!,
                          penaltyApplied: e.target.value as PenaltyAppliedEnum,
                        },
                      }))
                    }>
                    <option value={PenaltyAppliedEnum.IGNORE}>
                      {intl.formatMessage(messages.penaltyIgnore)}
                    </option>
                    <option value={PenaltyAppliedEnum.HAS_PENALTY_APPLIED}>
                      {intl.formatMessage(messages.penaltyHas)}
                    </option>
                    <option value={PenaltyAppliedEnum.NO_PENALTY_APPLIED}>
                      {intl.formatMessage(messages.penaltyNo)}
                    </option>
                  </Form.Control>
                </Col>
              </Form.Group>

              <Form.Group as={Row} className="mb-3">
                <Form.Label column sm={4}>
                  <FormattedMessage {...messages.includedDates} />
                </Form.Label>
                <Col sm={8}>
                  <DateRangesInput
                    value={formData.sellingActivationRule?.includedDates || []}
                    onChange={ranges =>
                      setFormData(prev => ({
                        ...prev,
                        sellingActivationRule: {
                          ...prev.sellingActivationRule!,
                          includedDates: ranges as { start: Date; end: Date }[],
                        },
                      }))
                    }
                  />
                </Col>
              </Form.Group>

              <Form.Group as={Row} className="mb-3">
                <Form.Label column sm={4}>
                  <FormattedMessage {...messages.excludedDates} />
                </Form.Label>
                <Col sm={8}>
                  <DateRangesInput
                    value={formData.sellingActivationRule?.excludedDates || []}
                    onChange={ranges =>
                      setFormData(prev => ({
                        ...prev,
                        sellingActivationRule: {
                          ...prev.sellingActivationRule!,
                          excludedDates: ranges as { start: Date; end: Date }[],
                        },
                      }))
                    }
                  />
                </Col>
              </Form.Group>

              <Form.Group as={Row} className="mb-3">
                <Form.Label column sm={4}>
                  <FormattedMessage {...messages.promoCodes} />
                </Form.Label>
                <Col sm={8}>
                  <Form.Control
                    type="text"
                    value={formData.sellingActivationRule?.promoCodes?.join(", ") || ""}
                    onChange={e =>
                      setFormData(prev => ({
                        ...prev,
                        sellingActivationRule: {
                          ...prev.sellingActivationRule!,
                          promoCodes: e.target.value
                            .split(",")
                            .map(s => s.trim())
                            .filter(Boolean),
                        },
                      }))
                    }
                  />
                </Col>
              </Form.Group>

              <Form.Group as={Row} className="mb-3">
                <Form.Label column sm={4}>
                  <FormattedMessage {...messages.includedTags} />
                </Form.Label>
                <Col sm={8}>
                  <Form.Control
                    type="text"
                    value={formData.sellingActivationRule?.includedTags?.join(", ") || ""}
                    onChange={e =>
                      setFormData(prev => ({
                        ...prev,
                        sellingActivationRule: {
                          ...prev.sellingActivationRule!,
                          includedTags: e.target.value
                            .split(",")
                            .map(s => s.trim())
                            .filter(Boolean),
                        },
                      }))
                    }
                  />
                </Col>
              </Form.Group>
            </>
          )}

          {error && <Alert variant="danger">{error}</Alert>}
        </Form>
      </StyledModalBody>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          <FormattedMessage {...messages.back} />
        </Button>
        <Button variant="primary" onClick={handleSave}>
          <FormattedMessage {...messages.save} />
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
